import type { CareStateStateUpdateT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { applyCareStateOperations } from '../lib/apply.js';
import type { CareStateRow, CareStateUpdate } from '../model/types.js';
import type {
  CareStateClient,
  CareStateCursor,
  StreamingConnectionOptions,
  UpdateReply,
} from '../model/index.js';

import { ENV, logger } from '@/shared';

import { decodeCareStateUpdateReply, encodeCareStateUpdate } from './codecs.js';
import type { CareStateApi } from './api.js';
import type { CareStateUpdateDto } from './api.js';

function tableCounts(state: unknown): Record<string, number> {
  if (state === null || typeof state !== 'object') return {};
  const entries = Object.entries(state as Record<string, unknown>);
  return Object.fromEntries(
    entries.filter(([, v]) => Array.isArray(v)).map(([k, v]) => [k, (v as unknown[]).length])
  );
}

export type CreateCareStateClientOptions = {
  userId: string;
  api?: CareStateApi;
  updateDebounceInterval?: number;
};

export function createCareStateClient<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  options: CreateCareStateClientOptions
): CareStateClient<DbT> {
  const api = options.api ?? null;
  if (api === null) {
    throw new Error('CareState API is not configured (pass `api` to createCareStateClient)');
  }

  const updateDebounceInterval = options.updateDebounceInterval ?? 100;

  return {
    updateDebounceInterval,
    fetch: async () => {
      const { state, cursor } = await api.fetchState(options.userId);
      if (!ENV.isProduction) {
        logger.info('care state client snapshot fetched', {
          source: 'entities/care-state/api/createCareStateClient',
          userId: options.userId,
          cursor,
          tableCounts: tableCounts(state),
        });
      }
      return [state as unknown as DbT, cursor as CareStateCursor];
    },
    async *streamEvents(
      cursor: CareStateCursor,
      streamOptions?: StreamingConnectionOptions
    ): AsyncGenerator<CareStateStateUpdateT, void, unknown> {
      if (!ENV.isProduction) {
        logger.info('care state client stream starting', {
          source: 'entities/care-state/api/createCareStateClient',
          userId: options.userId,
          cursor,
        });
      }

      for await (const event of api.streamEvents(options.userId, cursor, {
        signal: streamOptions?.signal,
      })) {
        yield event;
      }

      if (!ENV.isProduction) {
        logger.info('care state client stream ended', {
          source: 'entities/care-state/api/createCareStateClient',
          userId: options.userId,
        });
      }
    },
    submit: async (updatesById: Record<string, CareStateUpdate<DbT>>) => {
      if (!ENV.isProduction) {
        const updateCountsByTypeAndTable = Object.values(updatesById).reduce<
          Record<string, Record<string, number>>
        >((acc, u) => {
          const typeKey = u.type;
          const tableKey = String(u.table);
          const bucket = (acc[typeKey] ??= {});
          bucket[tableKey] = (bucket[tableKey] ?? 0) + 1;
          return acc;
        }, {});
        logger.info('care state client submitting updates', {
          source: 'entities/care-state/api/createCareStateClient',
          userId: options.userId,
          updateCount: Object.keys(updatesById).length,
          updateCountsByTypeAndTable,
        });
      }

      const updateDtos: Record<string, CareStateUpdateDto> = {};
      for (const [id, update] of Object.entries(updatesById)) {
        updateDtos[id] = encodeCareStateUpdate(update);
      }

      const { replies } = await api.postUpdates(options.userId, updateDtos);
      const mappedReplies: Record<string, UpdateReply> = {};
      for (const [id, reply] of Object.entries(replies)) {
        mappedReplies[id] = decodeCareStateUpdateReply(reply);
      }

      if (!ENV.isProduction) {
        const replyValues = Object.values(mappedReplies);
        const acceptedCursors = replyValues
          .filter(
            (r): r is Extract<UpdateReply, { type: 'acceptedUpdate' }> =>
              r.type === 'acceptedUpdate'
          )
          .map((r) => r.cursor);
        logger.info('care state client updates completed', {
          source: 'entities/care-state/api/createCareStateClient',
          userId: options.userId,
          replyCount: replyValues.length,
          acceptedCount: acceptedCursors.length,
          rejectedCount: replyValues.length - acceptedCursors.length,
          maxAcceptedCursor: acceptedCursors.length === 0 ? null : Math.max(...acceptedCursors),
        });
      }

      return mappedReplies;
    },
    applyEventsAndUpdates: (state, events, updates) =>
      applyCareStateOperations(state, events, updates),
  };
}
